import { invariant } from 'ts-invariant';
import type { DependencyGraphManager } from '../../../../dependency/manager/DependencyGraphManager';
import { BlockHelper } from '../../../../helpers/BlockHelper';
import { PageFunctionHelper } from '../../../../helpers/PageFunctionHelper';
import DataSourceHelper from '../../../helper/DataSourceHelper';
import type BlocksRegistry from '../../RegistryProvider/BlocksRegistry';
import {
  blockDependencyBuilder,
  dataSourceDependencyBuilder,
  pageFunctionDependencyBuilder,
} from '../builders';
import type { EntityListType } from '../types';
import {
  applySavedPageFieldDependencies,
  buildBlocksImplicitDependencies,
  buildEntityDataSourceDependencyGraph,
  buildPageFieldDependencies,
  buildPageFunctionsImplicitDependencies,
} from './utils/dependencyPasses';

/**
 * Returns the two graph-build passes for a given interface page:
 *   - handleSavedDependencies: use dependsOn saved on the page (fast path).
 *   - buildNewDependencies: recompute dependsOn from live entity state (slow path).
 *
 * Both share the implicit-deps / entity-data-source / page-field passes defined
 * in ./utils/dependencyPasses. Which one runs is decided by the page's
 * `shouldUseBuiltDependencies` flag in buildInstanceContext.
 */
export function getHandleSavedDependencies(registry: BlocksRegistry, interfacePageId: string) {
  function handleSavedDependencies({
    entityList,
    graphManager,
  }: {
    entityList: EntityListType;
    graphManager: DependencyGraphManager;
  }) {
    const blocks = entityList.blocks;
    const blockValues = Object.values(blocks);
    for (let blockIndex = 0; blockIndex < blockValues.length; blockIndex++) {
      const block = blockValues[blockIndex];
      if (!block) continue;
      graphManager.updateNode(block.id, BlockHelper.getDependsOn(block));
    }
    const pageFunctionEntries = Object.entries(entityList.pageFunctions ?? {});
    for (
      let pageFunctionIndex = 0;
      pageFunctionIndex < pageFunctionEntries.length;
      pageFunctionIndex++
    ) {
      const [pageFunctionId, pageFunction] = pageFunctionEntries[pageFunctionIndex];
      if (!pageFunction) continue;
      graphManager.updateNode(pageFunctionId, PageFunctionHelper.getDependsOn(pageFunction));
    }
    const dataSourceEntries = Object.entries(entityList.dataSources);
    for (let dataSourceIndex = 0; dataSourceIndex < dataSourceEntries.length; dataSourceIndex++) {
      const [dataSourceId, dataSource] = dataSourceEntries[dataSourceIndex];
      if (!dataSource) continue;
      graphManager.updateNode(dataSourceId, DataSourceHelper.getDependsOn(dataSource));
    }
    buildEntityDataSourceDependencyGraph({
      entityDataSources: entityList.entityDataSources,
      graphManager,
    });
    buildBlocksImplicitDependencies({ blocks, graphManager, interfacePageId, registry });
    buildPageFunctionsImplicitDependencies({
      pageFunctions: entityList.pageFunctions,
      graphManager,
    });
    applySavedPageFieldDependencies({ entityList, graphManager });
  }

  function buildNewDependencies({
    entityList,
    graphManager,
  }: {
    entityList: EntityListType;
    graphManager: DependencyGraphManager;
  }) {
    const blocks = entityList.blocks;
    const blockStateEntries = Object.entries(entityList.blocksState);
    for (let blockStateIndex = 0; blockStateIndex < blockStateEntries.length; blockStateIndex++) {
      const [, blockState] = blockStateEntries[blockStateIndex];
      if (!blockState) continue;
      const blockId = blockState.originalBlockId ?? blockState.id;
      if (!blockId) continue;
      const block = blocks[blockId];
      invariant(block, 'Page block not found');
      graphManager.updateNodes(blockDependencyBuilder({ blockState, block }));
    }
    const pageFunctionEntries = Object.entries(entityList.pageFunctions);
    for (
      let pageFunctionIndex = 0;
      pageFunctionIndex < pageFunctionEntries.length;
      pageFunctionIndex++
    ) {
      const [, pageFunction] = pageFunctionEntries[pageFunctionIndex];
      if (!pageFunction) continue;
      graphManager.updateNodes(pageFunctionDependencyBuilder({ pageFunction }));
    }
    const dataSourceEntries = Object.entries(entityList.dataSources);
    for (let dataSourceIndex = 0; dataSourceIndex < dataSourceEntries.length; dataSourceIndex++) {
      const [, dataSource] = dataSourceEntries[dataSourceIndex];
      if (!dataSource) continue;
      graphManager.updateNodes(dataSourceDependencyBuilder({ dataSource }));
    }
    buildEntityDataSourceDependencyGraph({
      entityDataSources: entityList.entityDataSources,
      graphManager,
    });
    buildBlocksImplicitDependencies({ blocks, graphManager, interfacePageId, registry });
    buildPageFunctionsImplicitDependencies({
      pageFunctions: entityList.pageFunctions,
      graphManager,
    });
    buildPageFieldDependencies({ entityList, graphManager });
  }

  return { handleSavedDependencies, buildNewDependencies };
}
